/**
 * @module services/urgency-waiting-room
 * @description Helpers puros de la sala de espera de urgencias (UC-REG-282):
 *              texto de la estimación que devuelve la API y estado de
 *              presencia del paciente a partir de su último heartbeat.
 *              Extraídos para testearlos sin montar los SFC.
 */

/** @description Segundos sin heartbeat a partir de los cuales el paciente se considera "ausente" */
export const AWAY_AFTER_SECONDS = 45;
/** @description Segundos sin heartbeat a partir de los cuales se considera "sin conexión" */
export const OFFLINE_AFTER_SECONDS = 180;

/**
 * @description Texto humano de la estimación de espera.
 * @param {{ minutes: number, min: number, max: number }|null} eta - `estimatedWait` de la API
 * @returns {string}
 */
export function formatEta(eta) {
  if (!eta) return "—";
  if (eta.minutes === 0) return "unos minutos";
  if (eta.max > 60) return "más de 1 hora";
  if (eta.min === eta.max) return `${eta.max} min`;
  return `${eta.min} a ${eta.max} min`;
}

/**
 * @description "Hay N personas antes que vos" según la cantidad adelante.
 * @param {number} ahead
 * @returns {string}
 */
export function formatAhead(ahead) {
  if (!ahead || ahead <= 0) return "Sos el próximo";
  if (ahead === 1) return "Hay 1 persona antes que vos";
  return `Hay ${ahead} personas antes que vos`;
}

/**
 * @description Estado de presencia del paciente en la sala de espera.
 * @param {string|Date|null} lastSeenAt - `patientLastSeenAt` del caso
 * @param {number} [now=Date.now()] - Reloj (inyectable para tests)
 * @returns {{ key: 'online'|'away'|'offline'|'never', label: string }}
 */
export function presenceState(lastSeenAt, now = Date.now()) {
  if (!lastSeenAt) return { key: "never", label: "No entró a la sala" };
  const seconds = Math.max(
    0,
    Math.floor((now - new Date(lastSeenAt).getTime()) / 1000)
  );
  if (seconds <= AWAY_AFTER_SECONDS)
    return { key: "online", label: "En línea" };
  if (seconds <= OFFLINE_AFTER_SECONDS) {
    return { key: "away", label: `Ausente hace ${formatAgo(seconds)}` };
  }
  return { key: "offline", label: `Sin conexión hace ${formatAgo(seconds)}` };
}

/**
 * @description Alertas sonoras que corresponden a un cambio de estado de la
 *              sala (UC-REG-283): `next` cuando pasa a quedar 1 persona (o
 *              ninguna) adelante, `taken` cuando el médico toma el caso. Sin
 *              estado previo (primera carga) no suena nada.
 * @param {{ status: string, ahead: number }|null} prev - Estado anterior
 * @param {{ status: string, ahead: number }|null} next - Estado nuevo
 * @returns {Array<'next'|'taken'>}
 */
export function transitionAlerts(prev, next) {
  if (!prev || !next) return [];
  const alerts = [];
  if (
    prev.status === "waiting" &&
    next.status === "waiting" &&
    prev.ahead > 1 &&
    next.ahead <= 1
  ) {
    alerts.push("next");
  }
  if (prev.status === "waiting" && next.status === "in-progress") {
    alerts.push("taken");
  }
  return alerts;
}

/** @description Secuencias de tonos (Hz, ms) por tipo de alerta */
const CHIMES = {
  next: [
    [660, 160],
    [880, 220],
  ],
  taken: [
    [660, 140],
    [880, 140],
    [1175, 320],
  ],
};

/**
 * @description Reproduce una alerta con WebAudio (sin archivos de audio).
 *              Necesita un AudioContext creado tras un gesto del usuario.
 * @param {AudioContext|null} ctx
 * @param {'next'|'taken'} kind
 */
export function playChime(ctx, kind) {
  const tones = CHIMES[kind];
  if (!ctx || !tones) return;
  let at = ctx.currentTime;
  tones.forEach(([freq, ms]) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(0.25, at + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + ms / 1000);
    osc.connect(gain).connect(ctx.destination);
    osc.start(at);
    osc.stop(at + ms / 1000 + 0.02);
    at += ms / 1000 + 0.06;
  });
}

/**
 * @description "40 s", "3 min", "2 h".
 * @param {number} seconds
 * @returns {string}
 */
export function formatAgo(seconds) {
  if (seconds < 60) return `${seconds} s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min`;
  return `${Math.floor(seconds / 3600)} h`;
}
